import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";

import { scoreSeverity } from "@/features/matches";
import { cn } from "@/shared/lib";
import type { Severity } from "@/shared/ui/severity-dot";

const RING_COLORS: Record<Severity, string> = {
  good: "border-good",
  warn: "border-warn",
  bad: "border-bad",
};

// `sm` — иконка находки-метрики, `md` — карточка последнего разбора на главной,
// `lg` — шапка экрана разбора.
const SIZES = {
  sm: { ring: cn("size-[56px] border-[3px]"), score: "text-[18px]", outOf: "text-[9px]" },
  md: { ring: cn("size-[68px] border-4"), score: "text-[22px]", outOf: "text-[10px]" },
  lg: { ring: cn("size-[84px] border-[5px]"), score: "text-[28px]", outOf: "text-[11px]" },
} as const;

const OUT_OF = msg({ id: "analysis.last.out_of", message: "of 100" });
const LABEL = msg({
  id: "analysis.last.score_label",
  message: "Match score: better than {score} of 100 players of your rank on this hero",
});

type ScoreRingProps = {
  score: number;
  size?: keyof typeof SIZES;
  // Цвет кольца; не задан — по шкале оценки матча (`scoreSeverity`).
  severity?: Severity;
  // Подпись для экранных дикторов; не задана — про оценку матча.
  label?: string;
};

// Оценка «из 100» в кольце, как в макете: число и «из 100», цвет — по оценке. Кроме оценки
// матча — перцентиль метрики в находке.
export const ScoreRing = ({ score, size = "md", severity, label }: ScoreRingProps) => {
  const { _ } = useLingui();
  const classes = SIZES[size];
  return (
    <div
      role="img"
      aria-label={label ?? _({ ...LABEL, values: { score } })}
      data-score={score}
      className={cn(
        "flex shrink-0 flex-col items-center justify-center rounded-full",
        classes.ring,
        RING_COLORS[severity ?? scoreSeverity(score)],
      )}
    >
      <span
        aria-hidden="true"
        className={cn("font-mono leading-none font-semibold", classes.score)}
      >
        {score}
      </span>
      <span aria-hidden="true" className={cn("mt-0.5 leading-none text-subtle", classes.outOf)}>
        {_(OUT_OF)}
      </span>
    </div>
  );
};
